import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { format } from "date-fns";
import { Users, CheckCircle, XCircle, FileText, Mail, Printer } from "lucide-react";

interface AttendanceRecord {
  id: string;
  schedule_id: string;
  student_email: string;
  student_name: string | null;
  attended: boolean;
  absence_reason: string | null;
  attendance_notes: string | null;
}

interface ScheduleWithAttendance {
  id: string;
  student_email: string;
  student_name: string | null;
  session_date: string;
  start_time: string;
  end_time: string;
  subject: string;
  topic: string | null;
  status: string;
  attendance?: AttendanceRecord;
}

interface AttendanceTrackerProps {
  scheduleId: string;
  sessionDate: string;
  subject: string;
  onClose?: () => void;
}

export default function AttendanceTracker({ scheduleId, sessionDate, subject, onClose }: AttendanceTrackerProps) {
  const { user } = useAuth();
  const [schedules, setSchedules] = useState<ScheduleWithAttendance[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [attendanceData, setAttendanceData] = useState<Record<string, {
    attended: boolean;
    absence_reason: string;
    attendance_notes: string;
  }>>({});

  useEffect(() => {
    loadSessionSchedules();
  }, [scheduleId]);

  const loadSessionSchedules = async () => {
    if (!user) return;

    try {
      // Get all schedules for the same date, time and subject (group session)
      const { data: schedule } = await supabase
        .from('tutoring_schedules')
        .select('session_date, start_time, end_time, subject')
        .eq('id', scheduleId)
        .single();

      if (!schedule) return;

      // Get all schedules for this session
      const { data: sessionSchedules, error } = await supabase
        .from('tutoring_schedules')
        .select(`
          id,
          student_email,
          student_name,
          session_date,
          start_time,
          end_time,
          subject,
          topic,
          status
        `)
        .eq('instructor_id', user.id)
        .eq('session_date', schedule.session_date)
        .eq('start_time', schedule.start_time)
        .eq('end_time', schedule.end_time)
        .eq('subject', schedule.subject);

      if (error) throw error;

      // Get existing attendance records
      const scheduleIds = sessionSchedules?.map(s => s.id) || [];
      const { data: attendanceRecords } = await supabase
        .from('session_attendance')
        .select('*')
        .in('schedule_id', scheduleIds);

      // Combine schedules with attendance data
      const schedulesWithAttendance = sessionSchedules?.map(schedule => {
        const attendance = attendanceRecords?.find(a => a.schedule_id === schedule.id);
        return {
          ...schedule,
          attendance
        };
      }) || [];

      setSchedules(schedulesWithAttendance);

      // Initialize attendance data
      const initialAttendanceData: Record<string, any> = {};
      schedulesWithAttendance.forEach(schedule => {
        initialAttendanceData[schedule.id] = {
          attended: schedule.attendance?.attended ?? false,
          absence_reason: schedule.attendance?.absence_reason ?? '',
          attendance_notes: schedule.attendance?.attendance_notes ?? ''
        };
      });
      setAttendanceData(initialAttendanceData);

    } catch (error) {
      console.error('Error loading session schedules:', error);
      toast.error('Failed to load attendance data');
    } finally {
      setLoading(false);
    }
  };

  const updateAttendance = (scheduleId: string, field: string, value: any) => {
    setAttendanceData(prev => ({
      ...prev,
      [scheduleId]: {
        ...prev[scheduleId],
        [field]: value
      }
    }));
  };

  const saveAttendance = async () => {
    if (!user) return;

    setSaving(true);
    try {
      const attendanceRecords = schedules.map(schedule => ({
        schedule_id: schedule.id,
        student_email: schedule.student_email,
        student_name: schedule.student_name,
        attended: attendanceData[schedule.id]?.attended ?? false,
        absence_reason: attendanceData[schedule.id]?.absence_reason || null,
        attendance_notes: attendanceData[schedule.id]?.attendance_notes || null,
        marked_by: user.id
      }));

      // Upsert attendance records
      const { error } = await supabase
        .from('session_attendance')
        .upsert(attendanceRecords, {
          onConflict: 'schedule_id',
          ignoreDuplicates: false
        });

      if (error) throw error;

      toast.success('Attendance saved successfully');
      onClose?.();
    } catch (error) {
      console.error('Error saving attendance:', error);
      toast.error('Failed to save attendance');
    } finally {
      setSaving(false);
    }
  };

  const generateAttendanceReport = () => {
    const report = {
      sessionDate,
      subject,
      totalStudents: schedules.length,
      attendedCount: Object.values(attendanceData).filter(a => a.attended).length,
      absentCount: Object.values(attendanceData).filter(a => !a.attended).length,
      students: schedules.map(schedule => ({
        name: schedule.student_name || schedule.student_email,
        email: schedule.student_email,
        attended: attendanceData[schedule.id]?.attended ?? false,
        absenceReason: attendanceData[schedule.id]?.absence_reason || '',
        notes: attendanceData[schedule.id]?.attendance_notes || ''
      }))
    };

    return report;
  };

  const printReport = () => {
    const report = generateAttendanceReport();
    const printWindow = window.open('', '_blank');
    
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>Attendance Report - ${report.sessionDate}</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 20px; }
              .header { text-align: center; margin-bottom: 30px; }
              .summary { margin-bottom: 20px; }
              .student-list { width: 100%; border-collapse: collapse; }
              .student-list th, .student-list td { 
                border: 1px solid #ddd; 
                padding: 8px; 
                text-align: left; 
              }
              .student-list th { background-color: #f5f5f5; }
              .attended { color: green; }
              .absent { color: red; }
            </style>
          </head>
          <body>
            <div class="header">
              <h1>Attendance Report</h1>
              <p><strong>Date:</strong> ${format(new Date(report.sessionDate), 'MMMM dd, yyyy')}</p>
              <p><strong>Subject:</strong> ${report.subject}</p>
            </div>
            
            <div class="summary">
              <h3>Summary</h3>
              <p>Total Students: ${report.totalStudents}</p>
              <p>Present: ${report.attendedCount}</p>
              <p>Absent: ${report.absentCount}</p>
            </div>
            
            <h3>Student Details</h3>
            <table class="student-list">
              <thead>
                <tr>
                  <th>Student Name</th>
                  <th>Email</th>
                  <th>Attendance</th>
                  <th>Absence Reason</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                ${report.students.map(student => `
                  <tr>
                    <td>${student.name}</td>
                    <td>${student.email}</td>
                    <td class="${student.attended ? 'attended' : 'absent'}">
                      ${student.attended ? 'Present' : 'Absent'}
                    </td>
                    <td>${student.absenceReason}</td>
                    <td>${student.notes}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  const emailReport = async () => {
    const report = generateAttendanceReport();
    
    try {
      // Get parent emails for absent students
      const absentStudents = report.students.filter(s => !s.attended);
      
      for (const student of absentStudents) {
        const { data: parentData } = await supabase
          .from('children')
          .select('parents(email, first_name, last_name)')
          .eq('student_email', student.email);

        if (parentData) {
          parentData.forEach(async (child) => {
            if (child.parents) {
              await supabase.functions.invoke('send-email', {
                body: {
                  to: child.parents.email,
                  subject: `Attendance Notice - ${student.name}`,
                  html: `
                    <h2>Attendance Notice</h2>
                    <p>Dear ${child.parents.first_name} ${child.parents.last_name},</p>
                    <p>This is to inform you that ${student.name} was absent from today's tutoring session.</p>
                    <p><strong>Session Details:</strong></p>
                    <ul>
                      <li>Date: ${format(new Date(report.sessionDate), 'MMMM dd, yyyy')}</li>
                      <li>Subject: ${report.subject}</li>
                    </ul>
                    ${student.absenceReason ? `<p><strong>Reason:</strong> ${student.absenceReason}</p>` : ''}
                    <p>Please contact us if you have any questions.</p>
                  `
                }
              });
            }
          });
        }
      }

      toast.success('Attendance reports sent to parents');
    } catch (error) {
      console.error('Error sending attendance reports:', error);
      toast.error('Failed to send attendance reports');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5" />
          Session Attendance
        </CardTitle>
        <CardDescription>
          Track attendance for {format(new Date(sessionDate), 'MMMM dd, yyyy')} - {subject}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Summary */}
        <div className="flex gap-4 text-sm">
          <Badge variant="outline" className="flex items-center gap-1">
            <Users className="h-3 w-3" />
            {schedules.length} Students
          </Badge>
          <Badge variant="outline" className="flex items-center gap-1">
            <CheckCircle className="h-3 w-3 text-green-500" />
            {Object.values(attendanceData).filter(a => a.attended).length} Present
          </Badge>
          <Badge variant="outline" className="flex items-center gap-1">
            <XCircle className="h-3 w-3 text-red-500" />
            {Object.values(attendanceData).filter(a => !a.attended).length} Absent
          </Badge>
        </div>

        {/* Student List */}
        <div className="space-y-4">
          {schedules.map((schedule) => (
            <div key={schedule.id} className="border rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Checkbox
                    checked={attendanceData[schedule.id]?.attended ?? false}
                    onCheckedChange={(checked) => 
                      updateAttendance(schedule.id, 'attended', checked)
                    }
                  />
                  <div>
                    <p className="font-medium">{schedule.student_name || schedule.student_email}</p>
                    <p className="text-sm text-muted-foreground">{schedule.student_email}</p>
                  </div>
                </div>
                <Badge variant={attendanceData[schedule.id]?.attended ? "default" : "destructive"}>
                  {attendanceData[schedule.id]?.attended ? 'Present' : 'Absent'}
                </Badge>
              </div>

              {!attendanceData[schedule.id]?.attended && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 ml-8">
                  <div className="space-y-2">
                    <Label htmlFor={`reason-${schedule.id}`}>Absence Reason</Label>
                    <Select
                      value={attendanceData[schedule.id]?.absence_reason || ''}
                      onValueChange={(value) => 
                        updateAttendance(schedule.id, 'absence_reason', value)
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select reason" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="illness">Illness</SelectItem>
                        <SelectItem value="family_emergency">Family Emergency</SelectItem>
                        <SelectItem value="other_commitment">Other Commitment</SelectItem>
                        <SelectItem value="no_reason">No Reason Given</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              <div className="ml-8">
                <div className="space-y-2">
                  <Label htmlFor={`notes-${schedule.id}`}>Notes</Label>
                  <Textarea
                    id={`notes-${schedule.id}`}
                    value={attendanceData[schedule.id]?.attendance_notes || ''}
                    onChange={(e) => 
                      updateAttendance(schedule.id, 'attendance_notes', e.target.value)
                    }
                    placeholder="Optional notes about attendance"
                    rows={2}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-2 justify-between">
          <div className="flex gap-2">
            <Button onClick={printReport} variant="outline" size="sm">
              <Printer className="h-4 w-4 mr-2" />
              Print Report
            </Button>
            <Button onClick={emailReport} variant="outline" size="sm">
              <Mail className="h-4 w-4 mr-2" />
              Email Parents
            </Button>
          </div>
          
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={saveAttendance} disabled={saving}>
              {saving ? 'Saving...' : 'Save Attendance'}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}